import { useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Section } from "@/components/Common";

export function ActivityList({ items = [], testId = "activity-list" }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  return (
    <ol className="space-y-3" data-testid={testId}>
      {items.map((a) => (
        <li key={a.id} className="border-l-2 border-secondary pl-3">
          <p className="text-sm">{a.action}</p>
          {a.message && <p className="mt-1 whitespace-pre-wrap rounded bg-muted p-2 text-sm">{a.message}</p>}
          <p className="text-xs text-muted-foreground">{a.actor_name} · {fmtDateTime(a.created_at)}{a.visible_to_customer === false && " · internal"}</p>
        </li>
      ))}
    </ol>
  );
}

export function NotesPanel({ notes = [], postUrl, onAdded }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(postUrl, { text });
      setText("");
      toast.success("Internal note saved");
      onAdded?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title={<span className="flex items-center gap-2"><Lock className="h-4 w-4" /> Internal notes</span>} testId="notes-panel">
      <p className="mb-3 text-xs text-muted-foreground">Visible to DJ Landscapes staff only - never shown to customers.</p>
      <Textarea aria-label="New internal note" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a private note…" data-testid="note-input" />
      <Button className="mt-2" size="sm" onClick={add} disabled={busy || !text.trim()} data-testid="note-add-btn">Add note</Button>
      <ul className="mt-4 space-y-3">
        {notes.map((n) => (
          <li key={n.id} className="rounded-md bg-muted p-3" data-testid="note-item">
            <p className="whitespace-pre-wrap text-sm">{n.text}</p>
            <p className="mt-1 text-xs text-muted-foreground">{n.author_name} · {fmtDateTime(n.created_at)}</p>
          </li>
        ))}
      </ul>
    </Section>
  );
}
